import { supabase } from '../../shared/supabase/supabaseClient';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { SaleAlreadyVoidedError } from '../domain/saleErrors';
import { StockInsufficientError } from '../../inventory/stock/domain/stockErrors';
import { getSaleDetail } from './salesRepoDetail';

export function isDuplicateSalePaymentConstraintError(raw: string): boolean {
  const msg = (raw || '').toLowerCase();
  return (
    msg.includes('sale_payments_sale_id_method_id_idx') ||
    (msg.includes('duplicate key') && msg.includes('sale_payments'))
  );
}

async function recoverProcessSaleCommandAfterDuplicatePayment(
  saleId: string,
): Promise<ProcessSaleCommandResult | null> {
  const prior = await getSaleDetail(saleId).catch(() => null);
  if (!prior || prior.status === 'voided') return null;
  return {
    applied: true,
    counterNumber: prior.counterNumber ?? null,
    pickupName: prior.pickupName ?? null,
    fulfillmentType: prior.fulfillmentType ?? null,
  };
}

export type ProcessSaleCommandResult = {
  applied: true;
  counterNumber?: number | null;
  pickupName?: string | null;
  fulfillmentType?: string | null;
  saleDraftStatus?: 'not_requested' | 'consumed';
  customerCreditStatus?: 'applied' | 'not_applicable';
  commissionStatus?: 'accrued' | 'not_applicable';
};

export function buildProcessSaleCommandPayload(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const saleId = typeof payload.saleId === 'string' ? payload.saleId.trim() : '';
  return {
    ...payload,
    operationId:
      typeof payload.operationId === 'string' && payload.operationId.trim()
        ? payload.operationId.trim()
        : saleId
          ? `${saleId}:process`
          : crypto.randomUUID(),
  };
}

export function mapProcessSaleRemoteFailure(raw: string): Error {
  const msg = (raw || '').trim();
  if (/sale_already_voided/i.test(msg)) {
    return new SaleAlreadyVoidedError();
  }
  if (/stock_policy_block/i.test(msg)) {
    return new StockInsufficientError(
      'No hay stock suficiente para completar la venta.',
    );
  }
  if (/promo_discount_mismatch/i.test(msg)) {
    return new Error('El descuento cambió. Revisá el carrito e intentá de nuevo.');
  }
  if (/promo_usage_limit_reached/i.test(msg)) {
    return new Error('Se alcanzó el límite de usos de esa promo.');
  }
  if (/promo_not_applicable|promo_code_invalid/i.test(msg)) {
    return new Error('Esa promo no aplica a este carrito.');
  }
  if (/promo_stale|promo_claim_invalid/i.test(msg)) {
    return new Error('La promo se venció. Revisá el carrito e intentá de nuevo.');
  }
  if (/promo_permission_denied/i.test(msg)) {
    return new Error('Hace falta permiso o PIN para esa promo.');
  }
  if (/module_permission_denied.*discount|supervisor_override/i.test(msg)) {
    return new Error('Hace falta permiso o PIN para ese descuento.');
  }
  const menuOptMatch = msg.match(/sale_menu_option_invalid:\s*(.+)$/i);
  if (menuOptMatch?.[1]?.trim()) {
    return new Error(menuOptMatch[1].trim());
  }
  if (/package_customer_required/i.test(msg)) {
    return new Error('El paquete necesita un cliente. Elegilo arriba del cobro.');
  }
  if (/package_price_mismatch/i.test(msg)) {
    return new Error('El precio del paquete cambió. Sacalo del carrito y agregalo de nuevo.');
  }
  if (/package_quantity_invalid/i.test(msg)) {
    return new Error('La cantidad del paquete no es válida.');
  }
  if (/package_not_available|package_id_required/i.test(msg)) {
    return new Error('Ese paquete ya no está disponible en esta sucursal.');
  }
  if (/capability not enabled:?\s*service_packages/i.test(msg)) {
    return new Error('Los paquetes no están habilitados en este negocio.');
  }
  // Errores de negocio con mapping dedicado en showCheckoutError (checkout POS):
  // pasan crudos para que el handler traduzca `code: detalle` al copy correcto.
  if (/combo_not_available|sale_price_mismatch|sale_total_mismatch/i.test(msg)) {
    return new Error(msg);
  }
  // Nunca pasar texto/códigos internos del backend al usuario.
  return new Error('No se pudo confirmar la venta. Reintentá o pedile a soporte.');
}

export async function processSaleCommandRemote(
  payload: Record<string, unknown>,
): Promise<ProcessSaleCommandResult> {
  const commandPayload = buildProcessSaleCommandPayload(payload);

  const discounts = Array.isArray(commandPayload.discounts) ? commandPayload.discounts : [];
  const hasPromoDiscount = discounts.some(
    (d) =>
      d &&
      typeof d === 'object' &&
      typeof (d as { promotionId?: unknown }).promotionId === 'string' &&
      String((d as { promotionId: string }).promotionId).trim().length > 0,
  );

  if (hasPromoDiscount) {
    const items = Array.isArray(commandPayload.items) ? commandPayload.items : [];
    const promoIntent = (commandPayload.promoIntent ?? {}) as {
      promoCodes?: string[];
      selectedPromotionIds?: string[];
    };
    const hashPayload = {
      items: items.map((it) => {
        const row = (it ?? {}) as Record<string, unknown>;
        return {
          productId: row.productId,
          variantId: row.variantId ?? null,
          quantity: row.quantity,
          price: row.price,
        };
      }),
      promoIntent: {
        promoCodes: promoIntent.promoCodes ?? [],
        selectedPromotionIds: promoIntent.selectedPromotionIds ?? [],
      },
    };
    // Evaluación: base PRE-promo (unitPriceOriginal), no el neto de línea.
    // hashPayload queda intacto para preservar la paridad de sale_promo_cart_hash.
    const evalItems = hashPayload.items.map((it, idx) => {
      const row = (items[idx] ?? {}) as Record<string, unknown>;
      return { ...it, unitPriceOriginal: row.unitPriceOriginal ?? it.price };
    });
    const { data: claimData, error: claimError } = await supabase.functions.invoke(
      'evaluate-sale-promotions',
      {
        body: {
          branchId: commandPayload.branchId,
          items: evalItems,
          promoIntent: hashPayload.promoIntent,
          hashPayload,
        },
      },
    );
    if (claimError || !claimData || typeof claimData !== 'object' || !(claimData as { claimId?: string }).claimId) {
      const failure = new Error('No se pudo validar la promo. Reintentá.');
      captureOpsFailure({
        module: 'pos',
        op: 'pos.sale.promo-claim',
        error: failure,
        ingestError: claimError?.message ?? 'promo_claim_failed',
        applied: false,
        path: 'web-ram',
        itemId: typeof payload.saleId === 'string' ? payload.saleId : undefined,
        extra: { branchId: typeof payload.branchId === 'string' ? payload.branchId : null },
      });
      throw failure;
    }
    commandPayload.promoClaimId = (claimData as { claimId: string }).claimId;
  }

  const { data, error } = await supabase.schema('pos').rpc('process_sale_command', {
    p_payload: commandPayload,
  });
  if (error) {
    const saleId = typeof payload.saleId === 'string' ? payload.saleId.trim() : '';
    if (saleId && isDuplicateSalePaymentConstraintError(error.message || '')) {
      const recovered = await recoverProcessSaleCommandAfterDuplicatePayment(saleId);
      if (recovered) return recovered;
    }

    const failure = mapProcessSaleRemoteFailure(error.message || '');
    captureOpsFailure({
      module: 'pos',
      op: 'pos.sale.process-command',
      error: failure,
      ingestError: error.message || failure.message,
      applied: false,
      path: 'web-ram',
      itemId: typeof payload.saleId === 'string' ? payload.saleId : undefined,
      extra: { branchId: typeof payload.branchId === 'string' ? payload.branchId : null },
    });
    throw failure;
  }

  const result = (data ?? {}) as {
    ok?: boolean;
    counter_number?: number | null;
    pickup_name?: string | null;
    fulfillment_type?: string | null;
    commission_result?: { ok?: boolean };
    sale_draft_status?: 'not_requested' | 'consumed';
    customer_credit_status?: 'applied' | 'not_applicable';
    commission_status?: 'accrued' | 'not_applicable';
  };
  if (result.ok !== true) {
    const failure = new Error('El servidor no confirmó la venta.');
    captureOpsFailure({
      module: 'pos',
      op: 'pos.sale.process-command',
      error: failure,
      ingestError: 'sale_command_not_applied',
      applied: false,
      path: 'web-ram',
      itemId: typeof payload.saleId === 'string' ? payload.saleId : undefined,
      extra: { branchId: typeof payload.branchId === 'string' ? payload.branchId : null },
    });
    throw failure;
  }

  return {
    applied: true,
    counterNumber: result.counter_number ?? null,
    pickupName: result.pickup_name ?? null,
    fulfillmentType: result.fulfillment_type ?? null,
    saleDraftStatus: result.sale_draft_status,
    customerCreditStatus: result.customer_credit_status,
    commissionStatus: result.commission_status,
  };
}
